import type { NavigationGuard } from 'vue-router'
import { useAccountStore } from '@/stores/account.ts'
import { routeNames } from '@/router/names.ts'
import { useSubAccountStore } from '@/stores/subAccount.ts'
import { routeNamesByDocumentCode } from '@/config/requiredDocumentsCodes.ts'
import {
    findAssignedSubAccount,
    getAssignedSubAccountIds,
    requiresCurrentTermsAcceptance,
} from '@/helpers/guardianTerms.ts'

export const authGuard: NavigationGuard = async (to, from, next) => {
    const accountStore = useAccountStore()
    const subAccountStore = useSubAccountStore()

    const subAccountIdFromRoute = to.params.subAccountId ? String(to.params.subAccountId) : null
    const isTermsRoute = Boolean(to.meta.isTermsRoute)

    if (
        accountStore.accountData &&
        subAccountStore.subAccountsOptions.length &&
        subAccountIdFromRoute &&
        getAssignedSubAccountIds(accountStore.accountData).includes(subAccountIdFromRoute)
    ) {
        if (!accountStore.accountData.childProtectAcceptedAt) {
            return next({
                name: routeNames.CHILD_PROTECTION_VIEW_NAME,
            })
        }

        const assignedSubAccount = findAssignedSubAccount(
            accountStore.accountData,
            subAccountIdFromRoute,
        )

        if (!isTermsRoute && requiresCurrentTermsAcceptance(assignedSubAccount)) {
            return next({
                name: routeNames.GUARDIAN_TERMS_VIEW_NAME,
                params: {
                    subAccountId: subAccountIdFromRoute,
                },
            })
        }

        return next()
    }

    const { isSuccess: isChildProtectStatusLoadSuccess, childProtectAccepted } =
        await accountStore.loadChildProtectAcceptedStatus()

    if (!isChildProtectStatusLoadSuccess) {
        accountStore.logout(true)

        return next({
            name: routeNames.LOGIN_VIEW_NAME,
        })
    }

    if (!childProtectAccepted) {
        return next({
            name: routeNames.CHILD_PROTECTION_VIEW_NAME,
        })
    }

    const {
        isSuccess: isAccountDataLoadSuccess,
        documentToAccept: documentToAccept1,
        currentTermsNotAccepted: accountCurrentTermsNotAccepted,
    } = await accountStore.loadAccountData()

    if (documentToAccept1) {
        return next({
            name: routeNamesByDocumentCode[documentToAccept1],
        })
    }

    if (accountCurrentTermsNotAccepted) {
        return next(false)
    }

    if (!isAccountDataLoadSuccess) {
        accountStore.logout(true)

        return next({
            name: routeNames.LOGIN_VIEW_NAME,
        })
    }

    const assignedSubAccountIds = getAssignedSubAccountIds(accountStore.accountData)
    const targetSubAccountId =
        subAccountIdFromRoute && assignedSubAccountIds.includes(subAccountIdFromRoute)
            ? subAccountIdFromRoute
            : assignedSubAccountIds[0]

    if (!targetSubAccountId) {
        accountStore.logout(true)

        return next({
            name: routeNames.LOGIN_VIEW_NAME,
        })
    }

    const assignedSubAccount = findAssignedSubAccount(accountStore.accountData, targetSubAccountId)

    if (requiresCurrentTermsAcceptance(assignedSubAccount)) {
        if (isTermsRoute && subAccountIdFromRoute === targetSubAccountId) {
            return next()
        }

        return next({
            name: routeNames.GUARDIAN_TERMS_VIEW_NAME,
            params: {
                subAccountId: targetSubAccountId,
            },
        })
    }

    if (isTermsRoute) {
        return next({
            name: routeNames.HOME_VIEW_NAME,
            params: {
                subAccountId: targetSubAccountId,
            },
        })
    }

    subAccountStore.setSubAccountsOptionsFromAccountData(accountStore.accountData)

    if (subAccountIdFromRoute && subAccountIdFromRoute === targetSubAccountId) {
        return next()
    }

    return next({
        name: routeNames.HOME_VIEW_NAME,
        params: {
            subAccountId: targetSubAccountId,
        },
    })
}
